import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import WhiteLabelWorkspaceShell from "../Components/WhiteLabelWorkspaceShell";
import ScrollableTableContainer from "../../../components/common/ScrollableTableContainer";
import {
  useCreateWhiteLabelDomainMutation,
  useGetWhiteLabelDomainsByCompanyQuery,
  useUpdateWhiteLabelDomainMutation,
} from "../../../redux/admin/whiteLabelApi";
import {
  DOMAIN_REGEX,
  getApiErrorMessage,
  IPV4_REGEX,
  mapValidationErrors,
} from "../Utils/whiteLabelFormUtils";
import { toast } from "react-hot-toast";

const DOMAIN_TYPES = ["crm", "website", "api"];
const DOMAIN_STATUSES = ["active", "inactive"];
const SSL_STATUSES = ["disabled", "pending", "active", "expired", "failed"];
const VERIFICATION_STATUSES = ["unverified", "pending", "verified", "failed"];

const EMPTY_FORM = Object.freeze({
  domainType: "crm",
  domain: "",
  isPrimary: false,
  isVerified: false,
  status: "active",
  dnsProvider: "",
  cname: "",
  aRecord: "",
  txtRecord: "",
  sslEnabled: false,
  sslProvider: "",
  sslStatus: "disabled",
  sslExpiry: "",
  verificationStatus: "unverified",
  lastVerifiedAt: "",
});

function normalizeDomainRecord(record) {
  return {
    domainType: record?.domainType || "crm",
    domain: record?.domain || "",
    isPrimary: Boolean(record?.isPrimary),
    isVerified: Boolean(record?.isVerified),
    status: record?.status || "active",
    dnsProvider: record?.dnsProvider || "",
    cname: record?.cname || "",
    aRecord: record?.aRecord || "",
    txtRecord: record?.txtRecord || "",
    sslEnabled: Boolean(record?.sslEnabled),
    sslProvider: record?.sslProvider || "",
    sslStatus: record?.sslStatus || "disabled",
    sslExpiry: record?.sslExpiry ? String(record.sslExpiry).slice(0, 10) : "",
    verificationStatus: record?.verificationStatus || "unverified",
    lastVerifiedAt: record?.lastVerifiedAt
      ? String(record.lastVerifiedAt).slice(0, 10)
      : "",
  };
}

function validateForm(form) {
  const errors = {};
  if (!form.domain || !DOMAIN_REGEX.test(form.domain)) {
    errors.domain = "Enter a valid domain.";
  }
  if (form.cname && !DOMAIN_REGEX.test(form.cname)) {
    errors.cname = "Enter a valid CNAME domain.";
  }
  if (form.aRecord && !IPV4_REGEX.test(form.aRecord)) {
    errors.aRecord = "Enter a valid IPv4 address.";
  }
  return errors;
}

function buildPayload(companyId, form) {
  return {
    companyId,
    domainType: form.domainType,
    domain: String(form.domain || "").trim(),
    isPrimary: Boolean(form.isPrimary),
    isVerified: Boolean(form.isVerified),
    status: form.status,
    dnsProvider: String(form.dnsProvider || "").trim() || undefined,
    cname: String(form.cname || "").trim() || undefined,
    aRecord: String(form.aRecord || "").trim() || undefined,
    txtRecord: String(form.txtRecord || "").trim() || undefined,
    sslEnabled: Boolean(form.sslEnabled),
    sslProvider: String(form.sslProvider || "").trim() || undefined,
    sslStatus: form.sslStatus,
    sslExpiry: String(form.sslExpiry || "").trim() || undefined,
    verificationStatus: form.verificationStatus,
    lastVerifiedAt: String(form.lastVerifiedAt || "").trim() || undefined,
  };
}

export default function WhiteLabelDomainsPage() {
  const theme = useTheme();
  const [companyId, setCompanyId] = useState("");
  const [dialogState, setDialogState] = useState({ open: false, domain: null });
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const {
    data: domains,
    isFetching,
    error,
    refetch,
  } = useGetWhiteLabelDomainsByCompanyQuery(companyId, {
    skip: !companyId,
  });

  const [createDomain, { isLoading: isCreating }] =
    useCreateWhiteLabelDomainMutation();
  const [updateDomain, { isLoading: isUpdating }] =
    useUpdateWhiteLabelDomainMutation();

  const isSubmitting = isCreating || isUpdating;

  useEffect(() => {
    if (!dialogState.open) {
      setForm(EMPTY_FORM);
      setFieldErrors({});
      setSubmitError("");
      return;
    }

    setForm(normalizeDomainRecord(dialogState.domain));
    setFieldErrors({});
    setSubmitError("");
  }, [dialogState]);

  const errorMessage = error
    ? getApiErrorMessage(error, "Failed to load domains.")
    : "";

  const handleSave = async () => {
    const nextErrors = validateForm(form);
    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      return;
    }

    try {
      const payload = buildPayload(companyId, form);
      if (dialogState.domain?._id) {
        await updateDomain({
          domainId: dialogState.domain._id,
          companyId,
          ...payload,
        }).unwrap();
        toast.success("Domain updated successfully.");
      } else {
        await createDomain(payload).unwrap();
        toast.success("Domain created successfully.");
      }
      setDialogState({ open: false, domain: null });
      refetch();
    } catch (submitErrorValue) {
      setFieldErrors((prev) => ({
        ...prev,
        ...mapValidationErrors(submitErrorValue),
      }));
      const message = getApiErrorMessage(
        submitErrorValue,
        "Unable to save domain."
      );
      setSubmitError(message);
      toast.error(message);
    }
  };

  const handleSetPrimary = async (record) => {
    try {
      await updateDomain({
        domainId: record._id,
        companyId,
        isPrimary: true,
        domainType: record.domainType,
      }).unwrap();
      toast.success("Primary domain updated.");
      refetch();
    } catch (updateError) {
      toast.error(getApiErrorMessage(updateError, "Unable to set primary domain."));
    }
  };

  return (
    <WhiteLabelWorkspaceShell
      title="Domain Manager"
      description="Manage company domains using the frozen Domain backend APIs. Delete remains disabled because the backend route does not exist."
      companyId={companyId}
      onCompanyChange={setCompanyId}
      error={errorMessage}
      actions={
        <Button
          variant="contained"
          onClick={() => setDialogState({ open: true, domain: null })}
          disabled={!companyId}
        >
          Add Domain
        </Button>
      }
    >
      {!companyId ? (
        <Alert severity="info">Select a company to manage its domains.</Alert>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${alpha(theme.palette.divider, 0.88)}`,
          }}
        >
          {isFetching ? (
            <Alert severity="info" sx={{ m: 2 }}>
              Loading company domains...
            </Alert>
          ) : !domains?.length ? (
            <Alert severity="info" sx={{ m: 2 }}>
              No domains configured for this company yet.
            </Alert>
          ) : (
            <ScrollableTableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {["Type", "Domain", "Primary", "Verification", "SSL", "Status", "Actions"].map((label) => (
                      <TableCell key={label} sx={{ fontWeight: 900 }}>
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {domains.map((record) => (
                    <TableRow key={record._id} hover>
                      <TableCell sx={{ textTransform: "uppercase", fontWeight: 700 }}>
                        {record.domainType}
                      </TableCell>
                      <TableCell>{record.domain}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={record.isPrimary ? "Primary" : "Secondary"}
                          color={record.isPrimary ? "success" : "default"}
                          variant={record.isPrimary ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={record.verificationStatus}
                          color={record.isVerified ? "success" : "warning"}
                          variant={record.isVerified ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={record.sslStatus}
                          color={record.sslStatus === "active" ? "success" : "default"}
                          variant={record.sslStatus === "active" ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={record.status}
                          color={record.status === "active" ? "success" : "default"}
                          variant={record.status === "active" ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setDialogState({ open: true, domain: record })
                              }
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Set Primary">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleSetPrimary(record)}
                                disabled={record.isPrimary}
                              >
                                <StarRoundedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Backend Not Available">
                            <span>
                              <IconButton size="small" disabled color="error">
                                <DeleteOutlineRoundedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollableTableContainer>
          )}
        </Paper>
      )}

      <Dialog
        open={dialogState.open}
        onClose={() => (isSubmitting ? null : setDialogState({ open: false, domain: null }))}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          {dialogState.domain?._id ? "Edit Domain" : "Create Domain"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {submitError ? <Alert severity="error">{submitError}</Alert> : null}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Domain Type"
                  value={form.domainType}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, domainType: event.target.value }))
                  }
                >
                  {DOMAIN_TYPES.map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Domain"
                  value={form.domain}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, domain: event.target.value }))
                  }
                  error={Boolean(fieldErrors.domain)}
                  helperText={fieldErrors.domain || " "}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isPrimary}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, isPrimary: event.target.checked }))
                      }
                    />
                  }
                  label="Primary Domain"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isVerified}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, isVerified: event.target.checked }))
                      }
                    />
                  }
                  label="Verified"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.sslEnabled}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, sslEnabled: event.target.checked }))
                      }
                    />
                  }
                  label="SSL Enabled"
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={form.status}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, status: event.target.value }))
                  }
                >
                  {DOMAIN_STATUSES.map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="SSL Status"
                  value={form.sslStatus}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, sslStatus: event.target.value }))
                  }
                >
                  {SSL_STATUSES.map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Verification Status"
                  value={form.verificationStatus}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      verificationStatus: event.target.value,
                    }))
                  }
                >
                  {VERIFICATION_STATUSES.map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="DNS Provider"
                  value={form.dnsProvider}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, dnsProvider: event.target.value }))
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="SSL Provider"
                  value={form.sslProvider}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, sslProvider: event.target.value }))
                  }
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="CNAME"
                  value={form.cname}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, cname: event.target.value }))
                  }
                  error={Boolean(fieldErrors.cname)}
                  helperText={fieldErrors.cname || " "}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="A Record"
                  value={form.aRecord}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, aRecord: event.target.value }))
                  }
                  error={Boolean(fieldErrors.aRecord)}
                  helperText={fieldErrors.aRecord || " "}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="TXT Record"
                  value={form.txtRecord}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, txtRecord: event.target.value }))
                  }
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="SSL Expiry"
                  InputLabelProps={{ shrink: true }}
                  value={form.sslExpiry}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, sslExpiry: event.target.value }))
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Last Verified At"
                  InputLabelProps={{ shrink: true }}
                  value={form.lastVerifiedAt}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      lastVerifiedAt: event.target.value,
                    }))
                  }
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDialogState({ open: false, domain: null })}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : "Save Domain"}
          </Button>
        </DialogActions>
      </Dialog>
    </WhiteLabelWorkspaceShell>
  );
}
